import { apiGet, apiPost, apiPut } from '../lib/api-client';

export interface OrderItem {
  id: number;
  productId: number;
  productName: string;
  productPrice: number;
  quantity: number;
  total: number;
  variant?: string;
}

export interface ShippingAddressInfo {
  id?: number;
  label?: string;
  name: string;
  phone: string;
  street: string;
  city: string;
  region: string;
  postalCode?: string;
}

export interface Order {
  id: number;
  orderNumber: string;
  userId: number;
  status: 'PENDING' | 'PAID' | 'PROCESSING' | 'SHIPPED' | 'DELIVERED' | 'CANCELLED' | 'REFUNDED';
  subtotal: number;
  shippingCost: number;
  tax: number;
  discount: number;
  total: number;
  paymentMethod?: string;
  paymentReference?: string;
  paidAt?: string;
  shippingAddressId?: number;
  shippingMethod?: string;
  trackingNumber?: string;
  notes?: string;
  adminNotes?: string;
  createdAt: string;
  updatedAt: string;
  items: OrderItem[];
  shippingAddress?: ShippingAddressInfo;
}

export interface CreateOrderData {
  shippingAddressId: number;
  shippingMethod: string;
  notes?: string;
  items: {
    productId: number;
    quantity: number;
    variant?: string;
  }[];
  subtotal: number;
  shippingCost: number;
  tax: number;
  discount: number;
  total: number;
}

export interface PaymentInitResponse {
  orderId: number;
  orderNumber: string;
  paymentUrl?: string;
  reference: string;
  accessCode?: string;
}

export interface ShippingMethod {
  id: number;
  name: string;
  description?: string;
  cost: number;
  estimatedDays?: string;
  isActive: boolean;
  sortOrder: number;
}

export const orderService = {
  async getShippingMethods(): Promise<ShippingMethod[]> {
    return apiGet<ShippingMethod[]>('/orders/shipping-methods');
  },

  async createOrder(data: CreateOrderData): Promise<Order> {
    return apiPost<Order>('/orders', data);
  },

  async initializePayment(orderId: number): Promise<PaymentInitResponse> {
    return apiPost<PaymentInitResponse>(`/orders/${orderId}/payment`);
  },

  async verifyPayment(orderId: number, reference: string): Promise<Order> {
    return apiPost<Order>(`/orders/${orderId}/verify`, { reference });
  },

  async getMyOrders(): Promise<Order[]> {
    return apiGet<Order[]>('/orders');
  },

  async getOrder(id: number): Promise<Order> {
    return apiGet<Order>(`/orders/${id}`);
  },

  async cancelOrder(id: number): Promise<Order> {
    return apiPut<Order>(`/orders/${id}/cancel`);
  },
};